import React from "react";
import { useHistory, useLocation, useParams } from "react-router";
import { OBJECT_TYPE } from "common/constants";
import { buildLink, getPath } from "common/routing";
import { getObjectDisplay } from "common-client/utils/customObject";
import {
  GetWorkflowsQuery,
  useGetWorkflowsQuery,
} from "../../../generated/graphql";
import { spacing } from "../../../stitches.config";
import { setTitle } from "../../../utils/title";
import { getCurrentURL } from "../../../utils/url";
import { FlexColumn, FlexRow } from "../../Common/__styles__/Layout";
import { Button } from "../../Common/Button";
import { StyleVariant, Tag } from "../../Common/Tag";
import { Body, Title } from "../../Common/Typography";
import { Card } from "../__styles__/Card";
import { Loading } from "../__styles__/Guest";
import AccessDeniedPage, { ACCESS_DENIED_RESOURCE } from "../AccessDeniedPage";
import Layout from "../Layout";
import { accountTitle } from "../utils";

export const ListWorkflows = () => {
  const history = useHistory();
  const location = useLocation<{ prevLocation?: string }>();
  const { workflowDefinitionId } = useParams<{
    workflowDefinitionId: string;
  }>();

  const { data, loading } = useGetWorkflowsQuery({
    variables: { workflowDefinitionId },
  });

  const title = data?.workflowDefinition?.name ?? "Loading...";
  setTitle(`${title} | ${accountTitle()}`);

  if (loading) {
    return <Loading />;
  }

  if (!data?.workflowDefinition) {
    return <AccessDeniedPage resource={ACCESS_DENIED_RESOURCE.WORKFLOW} />;
  }

  if (!data.workflows.length) {
    history.push(buildLink("workflowDefinition", { workflowDefinitionId }), {
      prevLocation: getCurrentURL(location),
    });
  }

  const prevLocation = location.state?.prevLocation ?? getPath("guestHome");
  return (
    <Layout hasGutters={true}>
      <FlexColumn
        style={{ margin: `${spacing.l.value} 0`, gap: spacing.l.value }}
      >
        <Button
          styleVariant="buttonLink"
          leftIconName="chevron-left"
          iconSpacing="xs"
          onClick={() => history.push(prevLocation)}
          style={{ width: "fit-content" }}
        >
          Back
        </Button>
        <FlexRow style={{ justifyContent: "space-between" }}>
          <Title size="large" type="semiBold">
            {data.workflowDefinition.name}
          </Title>
          <Button
            styleVariant="secondary"
            size="small"
            leftIconName="plus"
            onClick={() =>
              history.push(
                buildLink("createSubmission", {
                  submissionTypeId:
                    data.workflowDefinition?.initialSubmissionTypeId,
                }),
                { prevLocation: getCurrentURL(location) }
              )
            }
          >
            New
          </Button>
        </FlexRow>
        <FlexColumn style={{ gap: spacing.m.value }}>
          <Body size="default" type="emphasis">
            Applications
          </Body>
          <FlexColumn>
            {data.workflows.map(workflow => (
              <WorkflowCard key={workflow.id} workflow={workflow} />
            ))}
          </FlexColumn>
        </FlexColumn>
      </FlexColumn>
    </Layout>
  );
};

const WorkflowCard = ({
  workflow,
}: {
  workflow: GetWorkflowsQuery["workflows"][number];
}) => {
  const history = useHistory();
  const objectDisplay = workflow.property
    ? getObjectDisplay({
        objectType: OBJECT_TYPE.PROPERTY,
        object: workflow.property,
      })
    : null;

  return (
    <Card
      onClick={() =>
        history.push(buildLink("workflow", { workflowId: workflow.id }))
      }
    >
      <FlexColumn style={{ gap: spacing.xs.value }}>
        {objectDisplay && (
          <Body size="default" type="emphasis">
            {objectDisplay}
          </Body>
        )}
        {workflow.secondaryDisplay && (
          <Body size="default" type="regular" color="contentSecondary">
            {workflow.secondaryDisplay}
          </Body>
        )}
      </FlexColumn>
      <FlexRow>
        <Tag styleVariant={workflow.status.display as StyleVariant}>
          {workflow.status.text}
        </Tag>
      </FlexRow>
    </Card>
  );
};
